import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useDir } from "../../lib/useDir";
import { PageHeader, Loading, inputCls, Field } from "../../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

function GkkDialog({ g, dir, onClose, done }) {
  const [f, setF] = useState({ zone_id: g.zone_id || "Z1", name: g.name || "", locality: g.locality || "", address: g.address || "", active: g.active ?? true, reason: "" });
  const save = async () => {
    try { g.id ? await api.put(`/gkks/${g.id}`, f) : await api.post("/gkks", f); toast.success("GKK saved — past attendance records keep their original names"); done(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="gkk-dialog">
        <DialogHeader><DialogTitle className="font-serif">{g.id ? `Edit ${g.id}` : "Add GKK"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label="Zone (transfer requires official authorization)"><select className={inputCls} value={f.zone_id} onChange={(e) => setF({ ...f, zone_id: e.target.value })} data-testid="gkk-zone">{dir.zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}</select></Field>
          <Field label="GKK / Community name"><input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="gkk-name" /></Field>
          <Field label="Distinguishing locality"><input className={inputCls} value={f.locality} onChange={(e) => setF({ ...f, locality: e.target.value })} data-testid="gkk-locality" /></Field>
          <Field label="Official address"><input className={inputCls} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="gkk-address" /></Field>
          <label className="flex gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="gkk-active" />Active (uncheck to archive)</label>
          <Field label="Reason for change"><input className={inputCls} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="gkk-reason" /></Field>
          <button className="btn-sm btn-primary w-fit" disabled={!f.name} onClick={save} data-testid="gkk-save">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function GkkDirectory() {
  const { dir, reload } = useDir(true);
  const [edit, setEdit] = useState(null);
  const [unit, setUnit] = useState({});
  if (!dir) return <Loading />;
  const addUnit = async (gid) => {
    if (!unit[gid]) return;
    try { await api.post("/units", { gkk_id: gid, name: unit[gid] }); setUnit({ ...unit, [gid]: "" }); reload(); toast.success("Unit added"); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleUnit = async (u) => { try { await api.put(`/units/${u.id}`, { gkk_id: u.gkk_id, name: u.name, active: !u.active }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const addZone = async () => { const name = window.prompt("New zone name (e.g. Zone 7):"); if (name) { await api.post("/zones", { name, order: dir.zones.length + 1 }); reload(); } };
  return (
    <div>
      <PageHeader title="GKK Directory" sub={`Official roster imported from the parish workbook (GKK_Master): ${dir.gkks.length} GKKs across ${dir.zones.length} zones. Each GKK keeps a permanent ID.`}>
        <button className="btn-sm btn-ghost" onClick={addZone} data-testid="add-zone-btn">Add zone</button>
        <button className="btn-sm btn-primary" onClick={() => setEdit({})} data-testid="add-gkk-btn"><Plus className="h-4 w-4" />Add GKK</button>
      </PageHeader>
      <div className="grid gap-6">
        {dir.zones.map((z) => {
          const list = dir.gkks.filter((g) => g.zone_id === z.id);
          return (
            <section key={z.id} className="sfap-card overflow-hidden" data-testid={`zone-section-${z.id}`}>
              <div className="px-5 py-3 bg-[#2D3B2D] text-white flex justify-between"><b className="font-serif">{z.name}</b><span className="text-sm" data-testid={`zone-count-${z.id}`}>{list.filter((g) => g.active).length} GKKs</span></div>
              <div className="divide-y divide-[#F0EADC]">
                {list.map((g) => (
                  <div key={g.id} className={`px-5 py-3 grid gap-2 md:grid-cols-[90px_1fr_auto] ${g.active ? "" : "opacity-50"}`}>
                    <span className="font-mono text-xs text-stone-500 pt-1">{g.id}</span>
                    <div>
                      <div className="font-semibold">{g.name} <span className="text-stone-500 font-normal">– {g.locality}</span>{g.duplicate_name && <span className="ml-2 text-[10px] uppercase bg-[#F5F0E6] px-1.5 rounded">shared patron name</span>}{!g.active && " · archived"}</div>
                      <div className="text-sm text-stone-600">{g.address}</div>
                      <div className="flex flex-wrap gap-1.5 mt-1.5 items-center">
                        {dir.units.filter((u) => u.gkk_id === g.id).map((u) => <button key={u.id} onClick={() => toggleUnit(u)} title="Click to archive/restore" className={`text-xs rounded-full border px-2 py-0.5 ${u.active ? "border-[#2D3B2D]" : "line-through opacity-50"}`}>{u.name}</button>)}
                        <input className="h-7 text-xs rounded-md border border-[#D9CFB8] px-2 w-32" placeholder="+ Unit" value={unit[g.id] || ""} onChange={(e) => setUnit({ ...unit, [g.id]: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addUnit(g.id)} data-testid={`unit-input-${g.id}`} />
                      </div>
                    </div>
                    <button className="btn-sm btn-ghost h-8" onClick={() => setEdit(g)} data-testid={`edit-gkk-${g.id}`}><Pencil className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
      {edit && <GkkDialog g={edit} dir={dir} onClose={() => setEdit(null)} done={reload} />}
    </div>
  );
}
